import { Injectable, inject, signal, computed } from '@angular/core';
import { AdditionalInfoApi } from '../api/additional-info.api';
import { I18nService } from '../services/i18n.service';
import {
  GroupedAdditionalInfo,
  AdditionalInfo,
  AdditionalInfoType,
} from '../models/classes/additionalinfo';

@Injectable({ providedIn: 'root' })
export class AdditionalInfoStore {
  private additionalInfoApi = inject(AdditionalInfoApi);
  private i18n = inject(I18nService);

  private _loading = signal(false);
  private _infoTypes = signal<AdditionalInfoType[]>([]);
  private _defaultFields = signal<AdditionalInfo[]>([]);
  private _currentObjectId = signal<number | null>(null);
  private _groupedInfo = signal<GroupedAdditionalInfo[]>([]);
  private _resultCache = signal<Record<number, AdditionalInfo[]>>({});

  readonly loading = this._loading.asReadonly();
  readonly infoTypes = this._infoTypes.asReadonly();
  readonly defaultFields = this._defaultFields.asReadonly();
  readonly currentObjectId = this._currentObjectId.asReadonly();
  readonly groupedInfo = this._groupedInfo.asReadonly();

  readonly hasInfoTypes = computed(() => this._infoTypes().length > 0);

  /**
   * Get ultrasonic check defaults for current measurement system
   */
  readonly ultrasonicCheckDefaults = computed(() => {
    const measurementSystem = this.i18n.measureMentSystem() as string;
    return this._defaultFields()
      .filter((f) => f.infoTypeID === 4)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map((f) => ({
        ...f,
        currentMeasurementSystem: measurementSystem,
      }));
  });

  /**
   * Get rotating equipment defaults for current measurement system
   */
  readonly rotatingEquipmentDefaults = computed(() => {
    const measurementSystem = this.i18n.measureMentSystem() as string;
    return this._defaultFields()
      .filter((f) => f.infoTypeID === 5)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map((f) => ({
        ...f,
        currentMeasurementSystem: measurementSystem,
      }));
  });

  /**
   * Load additional info for an object with equipment type
   */
  async loadAdditionalInfo(
    objectID: number,
    equipmentType: string
  ): Promise<GroupedAdditionalInfo[]> {
    if (!objectID) {
      return [];
    }

    this._loading.set(true);
    this._currentObjectId.set(objectID);

    try {
      // Load info types if not already loaded
      if (!this.hasInfoTypes()) {
        await this.loadInfoTypes();
      }

      // Load additional info for this object
      const additionalInfo = await this.additionalInfoApi.getAdditionalInfo(objectID);

      // Process the response
      const processedInfo = this.processAdditionalInfoResponse(additionalInfo);

      // Cache the result
      this._resultCache.update((cache) => ({
        ...cache,
        [objectID]: processedInfo,
      }));

      // Group the information
      const grouped = this.groupAdditionalInformation(processedInfo, objectID, equipmentType);
      this._groupedInfo.set(grouped);

      return grouped;
    } catch (error) {
      console.error('Failed to load additional info:', error);
      this._groupedInfo.set([]);
      return [];
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Load info types and default fields
   */
  async loadInfoTypes(): Promise<void> {
    try {
      const response = await this.additionalInfoApi.getAdditionalInfoTypes();

      this._infoTypes.set(response.infoTypes || []);

      // Process default fields and convert to class instances
      const processedFields = (response.defaultFields || []).map((f) => {
        const dropdownParsed = f.dropdownValues
          ? this.parseJsonSafe(f.dropdownValues as unknown as string)
          : null;

        const processedField: Partial<AdditionalInfo> = {
          ...f,
          dropdownValues: Array.isArray(dropdownParsed)
            ? dropdownParsed.map((v: unknown) => JSON.stringify(v))
            : undefined,
          unit: f.unit ? this.parseJsonSafe(f.unit as unknown as string) : undefined,
          conversionFactor: f.conversionFactor
            ? this.parseJsonSafe(f.conversionFactor as unknown as string)
            : undefined,
          validations: f.validations
            ? this.parseJsonSafe(f.validations as unknown as string)
            : undefined,
          defaultValidation: f.defaultValidation
            ? this.parseJsonSafe(f.defaultValidation as unknown as string)
            : undefined,
          infoMessage: f.infoMessage
            ? this.parseJsonSafe(f.infoMessage as unknown as string)
            : undefined,
        } as Partial<AdditionalInfo>;

        // Convert to class instance
        return new AdditionalInfo(processedField);
      });

      this._defaultFields.set(processedFields);
    } catch (error) {
      console.error('Failed to load info types:', error);
    }
  }

  /**
   * Save additional info group
   */
  async saveAdditionalInfo(
    infoGroup: GroupedAdditionalInfo,
    objectID: number,
    equipmentType: string
  ): Promise<GroupedAdditionalInfo[]> {
    if (!infoGroup || !objectID) {
      return [];
    }

    this._loading.set(true);

    try {
      const response = await this.additionalInfoApi.updateAdditionalInfo(infoGroup.infoValues);

      // Convert response to class instances
      const updatedInfo = response.map((r) => new AdditionalInfo(r));

      // Update cache
      this._resultCache.update((cache) => ({
        ...cache,
        [objectID]: updatedInfo,
      }));

      // Regroup the information
      const grouped = this.groupAdditionalInformation(updatedInfo, objectID, equipmentType);
      this._groupedInfo.set(grouped);

      return grouped;
    } catch (error) {
      console.error('Failed to save additional info:', error);
      return [];
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Revert to cached values
   */
  revertChanges(objectID: number, equipmentType: string): GroupedAdditionalInfo[] {
    const cachedValues = this._resultCache()[objectID];
    if (!cachedValues) {
      return [];
    }

    const grouped = this.groupAdditionalInformation(cachedValues, objectID, equipmentType);
    this._groupedInfo.set(grouped);
    return grouped;
  }

  /**
   * Clear all state
   */
  clear(): void {
    this._loading.set(false);
    this._currentObjectId.set(null);
    this._groupedInfo.set([]);
  }

  /**
   * Clear cache for a specific object
   */
  clearCache(objectID?: number): void {
    if (objectID) {
      this._resultCache.update((cache) => {
        const { [objectID]: _removed, ...rest } = cache;
        void _removed; // Silence unused variable warning
        return rest;
      });
    } else {
      this._resultCache.set({});
    }
  }

  private parseJsonSafe(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private processAdditionalInfoResponse(additionalInfo: AdditionalInfo[]): AdditionalInfo[] {
    return additionalInfo.map((r) => {
      // Remove any formatting differences like whitespace
      try {
        r.text = r.text ? JSON.stringify(JSON.parse(r.text)) : '';
      } catch {
        // Keep original if parsing fails
      }
      // Convert to class instance
      return new AdditionalInfo(r);
    });
  }

  private groupAdditionalInformation(
    loadedValues: AdditionalInfo[],
    objectID: number,
    equipmentType: string
  ): GroupedAdditionalInfo[] {
    const availableValues = this.fillAdditionalInfo(loadedValues, objectID, equipmentType);
    const availableDisplayValues = this.fillDisplayInfo(loadedValues, objectID, equipmentType);

    const availableTypes = this.getAdditionalInfoDefault(objectID, equipmentType)
      .map((t) => t.infoTypeID)
      .filter((t, i, a) => a.indexOf(t) === i);

    const groupedInfoValues: GroupedAdditionalInfo[] = [];
    // harshit
    availableTypes.forEach((typeID) => {
      const foundInfoType = this._infoTypes().find((t) => t.id === typeID);
      if (!foundInfoType) return;
      groupedInfoValues.push({
        infoType: foundInfoType,
        infoValues: availableValues.filter((v) => v.infoTypeID === typeID),
        displayInfoValues: availableDisplayValues.filter(
          (v) => v.infoTypeID === typeID && (v.text || v.value)
        ),
      });
    });

    return groupedInfoValues;
  }

  private getAdditionalInfoDefault(objectID: number, equipmentType: string): AdditionalInfo[] {
    let equipmentTypeID = 0;

    switch (equipmentType?.toLowerCase()) {
      case 'flange':
        equipmentTypeID = 109;
        break;
      case 'pipe':
        equipmentTypeID = 22;
        break;
      case 'steamtrap':
      case 'steam trap':
        equipmentTypeID = 112;
        break;
    }

    return this._defaultFields()
      .filter((f) => f.equipmentTypeID === equipmentTypeID)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map((f) => {
        // Create a new class instance with updated properties
        return new AdditionalInfo({
          ...f,
          objectID: objectID,
          currentMeasurementSystem: this.i18n.measureMentSystem() as string,
          categoryID:
            ((f as unknown as Record<string, unknown>)['fieldID'] as number) ?? f.categoryID,
        });
      });
  }

  private fillAdditionalInfo(
    loadedValues: AdditionalInfo[],
    objectID: number,
    equipmentType: string
  ): AdditionalInfo[] {
    const tempAdditionalInfo = this.getAdditionalInfoDefault(objectID, equipmentType);

    loadedValues.forEach((resValue) => {
      const fieldIdx = tempAdditionalInfo.findIndex(
        (f) => f.categoryID === resValue.categoryID && f.infoTypeID === resValue.infoTypeID
      );

      if (fieldIdx < 0) return; // This only happens for broken or legacy data

      // Create new class instance with merged properties
      tempAdditionalInfo[fieldIdx] = new AdditionalInfo({
        ...tempAdditionalInfo[fieldIdx],
        additionalInfoID: resValue.additionalInfoID,
        value: resValue.value,
        text: resValue.text,
      });
    });

    return tempAdditionalInfo;
  }

  private fillDisplayInfo(
    loadedValues: AdditionalInfo[],
    objectID: number,
    equipmentType: string
  ): AdditionalInfo[] {
    const tempAdditionalInfo = this.getAdditionalInfoDefault(objectID, equipmentType);
    const result: AdditionalInfo[] = [];

    const compCategories = (cat1: string, cat2: string): boolean => {
      try {
        const ocat1 = JSON.parse(cat1);
        const ocat2 = JSON.parse(cat2);
        return JSON.stringify(ocat1['en']) === JSON.stringify(ocat2['en']);
      } catch {
        return false;
      }
    };

    loadedValues.forEach((resValue) => {
      const fieldIdx = tempAdditionalInfo.findIndex((f) => {
        return (
          compCategories(f.category, resValue.category) && f.infoTypeID === resValue.infoTypeID
        );
      });

      if (fieldIdx < 0) return; // This only happens for broken or legacy data

      // Create new class instance with merged properties
      tempAdditionalInfo[fieldIdx] = new AdditionalInfo({
        ...tempAdditionalInfo[fieldIdx],
        additionalInfoID: resValue.additionalInfoID,
        value: resValue.value,
        text: resValue.text,
      });

      result.push(tempAdditionalInfo[fieldIdx]);
    });

    return result;
  }
}
